/* ==========================================================================
   Boss Components — bc-search-fix.js
   Deployed 15 Aug 2026 · Internal Authority Audit Phase 1
   --------------------------------------------------------------------------
   THE BUG

   Impulse's PredictiveSearch.getSearchResults() builds its request as:

       { 'q': term, 'resources[limit]': 3, 'resources[limit_scope]': 'each' }

   It never sends `resources[type]`. Shopify's /search/suggest endpoint
   defaults to PRODUCTS ONLY when that parameter is absent, so the dropdown
   could never surface a collection, page or article — even though
   snippets/search-results.liquid already contains the markup to render them.

   Measured cost (Onsite Search tab, 180 days): 1,698 searches, 23 terms
   classified SEARCH FAILURE. The clearest case is
   "cz shadow 2 red dot mounts" — 10 searches, 0 clicks, against a collection
   of that exact name. The customer typed the collection title verbatim and
   the store could not show it to them.

   THE FIX

   Re-point the prototype method with `resources[type]` populated. This is an
   additive override rather than an edit to the 268KB theme.js, because:
     * the diff is auditable — you can read the whole change in one screen
     * it survives an Impulse theme update instead of being silently reverted
     * rollback is deleting one <script> tag

   `getSearchResults` is standard method shorthand on the prototype (not a
   class field, not constructor-bound), and both `onChange` and the debounced
   input handler resolve it dynamically at call time — so patching after the
   elements have upgraded still takes effect. Load order does not matter.

   ALSO FIXED HERE: there is no AbortController anywhere in theme.js, so
   in-flight suggest requests were never cancelled and slow responses could
   land after newer ones, painting stale results. Each request now supersedes
   the last.

   DELIBERATELY NOT INCLUDED: the `query` resource type (Shopify's search-term
   suggestions). The section markup supports it, but an unsupported or
   malformed resource type makes the endpoint 4xx, and the catch path closes
   the dropdown — i.e. a broken search box on a live revenue path. The four
   types below are the ones this theme is confirmed to render. Add 'query'
   later as a deliberate, separately tested change.

   ROLLBACK
   Remove the <script src="bc-search-fix.js"> line from
   snippets/predictive-search.liquid. Behaviour reverts to stock Impulse
   immediately.
   ========================================================================== */

(function () {
  'use strict';

  // Products must stay in this list or the dropdown loses its main content.
  var RESOURCE_TYPES = 'product,collection,page,article';

  function applyPatch() {
    if (!window.customElements || !window.customElements.get) return false;

    var PredictiveSearchEl = window.customElements.get('predictive-search');
    if (!PredictiveSearchEl) return false;
    if (PredictiveSearchEl.prototype.__bcSearchPatched) return true;

    if (typeof PredictiveSearchEl.prototype.getSearchResults !== 'function' || typeof PredictiveSearchEl.prototype.paramUrl !== 'function') return false;
    PredictiveSearchEl.prototype.__bcSearchPatched = true;

    PredictiveSearchEl.prototype.getSearchResults = function (searchTerm) {
      var self = this;
      var sequence = self.__bcSequence = (self.__bcSequence || 0) + 1;

      // Supersede any request still in flight for this element.
      if (self.__bcAbortController) {
        try { self.__bcAbortController.abort(); } catch (e) { /* no-op */ }
      }

      var controller = ('AbortController' in window) ? new AbortController() : null;
      self.__bcAbortController = controller;

      var searchObj = {
        'q': searchTerm,
        'resources[type]': RESOURCE_TYPES,
        'resources[limit]': 3,
        'resources[limit_scope]': 'each'
      };

      var params = self.paramUrl(searchObj);
      var endpoint = window.theme.routes.predictiveSearch +
                     '?' + params + '&section_id=search-results';

      fetch(endpoint, controller ? { signal: controller.signal } : undefined)
        .then(function (response) {
          if (!response.ok) throw new Error('Predictive search HTTP ' + response.status);
          return response.text();
        })
        .then(function (text) {
          if (sequence !== self.__bcSequence || self.input.value.trim() !== searchTerm.trim()) return;
          var section = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-search-results');

          // Defensive: if Shopify ever renames the wrapper, fail closed and
          // quietly rather than throwing on innerHTML of null.
          if (!section) { self.close(); return; }

          var suffix = '-' + (self.input.id || self.context || 'search');
          section.querySelectorAll('[id]').forEach(function (node) { node.id += suffix; });
          section.querySelectorAll('[aria-labelledby]').forEach(function (node) {
            node.setAttribute('aria-labelledby', node.getAttribute('aria-labelledby').split(' ').map(function (id) { return id + suffix; }).join(' '));
          });
          self.predictiveSearchResults.innerHTML = section.innerHTML;
          self.open();

          if (window.AOS && typeof window.AOS.refreshHard === 'function') {
            window.AOS.refreshHard();
          }
        })
        .catch(function (error) {
          if (sequence !== self.__bcSequence) return;
          // An aborted request is a newer keystroke, not a failure.
          if (error && error.name === 'AbortError') return;
          self.close();
          if (window.console && console.warn) {
            console.warn('[bc-search-fix] predictive search failed:', error);
          }
        });
    };

    return true;
  }

  if (!applyPatch()) {
    if (window.customElements && window.customElements.whenDefined) {
      window.customElements.whenDefined('predictive-search').then(applyPatch);
    } else {
      document.addEventListener('DOMContentLoaded', applyPatch);
    }
  }
})();
